// The central analytics service. The ONLY entry point for telemetry:
// components call track()/page()/identify() from here, never adapters directly
// (docs/ANALYTICS.md, docs/ENGINEERING.md principle 9).
// Consent-gated end to end; events before consent are dropped, not queued,
// except a tiny pre-consent buffer for the activation funnel that flushes
// only if consent is granted in the same visit.

import { ANALYTICS_SCHEMA_VERSION, type EventCatalog, type EventName } from "./events";
import { getConsent, onConsentChange, telemetryAllowed } from "./consent";
import { gaConsent, gaEvent, gaInit, gaPage, captureUtm } from "./ga";
import { mpAlias, mpIdentify, mpInit, mpReset, mpRevenue, mpSetSuper, mpTrack } from "./mixpanel";
import { newId } from "@/lib/ids";

const ANON_KEY = "bd.anon";
const APP_VERSION = "0.1.0";

let superProps: Record<string, unknown> = {};
const preConsent: Array<{ name: string; props: Record<string, unknown> }> = [];

export function anonymousId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = newId();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "ephemeral";
  }
}

export function initAnalytics(): void {
  const consented = telemetryAllowed();
  gaInit(consented);
  if (consented) start();

  onConsentChange((level) => {
    if (level === "all") {
      start();
      gaConsent(true);
      for (const e of preConsent.splice(0)) mpTrack(e.name, e.props);
    } else {
      gaConsent(false);
      mpReset();
    }
    track("consent.updated", { level: level === "all" ? "all" : "none" });
  });
}

function start(): void {
  mpInit();
  mpIdentify(anonymousId());
  superProps = {
    sv: ANALYTICS_SCHEMA_VERSION,
    appVersion: APP_VERSION,
    environment: import.meta.env.MODE,
    signedIn: false,
    theme: document.documentElement.dataset.theme ?? "dark",
    ...captureUtm(),
  };
  mpSetSuper(superProps);
}

/** Update a super property (theme change, sign-in, workspace pole, AI availability). */
export function setSuperProp(key: string, value: unknown): void {
  superProps[key] = value;
  if (telemetryAllowed()) mpSetSuper({ [key]: value });
}

export function track<N extends EventName>(name: N, props: EventCatalog[N]): void {
  const payload = { ...(props as Record<string, unknown>) };
  if (import.meta.env.DEV) {
    // Debug mode: every event visible in the console during development.
    console.debug(`%c◆ ${name}`, "color:#5CE8C8", payload);
  }
  if (!telemetryAllowed()) {
    if (getConsent() === "unset" && preConsent.length < 20) preConsent.push({ name, props: payload });
    return;
  }
  mpTrack(name, payload);
  gaEvent(name.replace(".", "_"), payload);
  if (name === "revenue.purchase") {
    const p = props as EventCatalog["revenue.purchase"];
    mpRevenue(p.amount, { packType: p.packType });
  }
}

export function page(path: string, title: string): void {
  if (!telemetryAllowed()) return;
  gaPage(path, title);
  mpTrack("page.viewed", { path, title });
}

/** Sign-in: merge anonymous history into the account identity. */
export function identifyAccount(accountId: string): void {
  setSuperProp("signedIn", true);
  if (!telemetryAllowed()) return;
  mpAlias(accountId, anonymousId());
  mpIdentify(accountId);
}

export function resetIdentity(): void {
  setSuperProp("signedIn", false);
  mpReset();
}
